/**
 * PageHeader - Consistent page header component with title and subtitle.
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const pageHeaderVariants = cva(
  'container mx-auto px-4 sm:px-6 max-w-6xl',
  {
    variants: {
      align: {
        left: 'text-left',
        center: 'text-center',
      },
    },
    defaultVariants: {
      align: 'center',
    },
  }
);

const titleVariants = cva(
  'font-bold tracking-tight leading-snug pb-1',
  {
    variants: {
      size: {
        sm: 'text-2xl sm:text-3xl',
        md: 'text-3xl sm:text-4xl',
        lg: 'text-3xl sm:text-4xl md:text-5xl',
      },
      gradient: {
        none: 'text-slate-900',
        brand: 'bg-gradient-to-r from-cyan-800 via-blue-800 to-purple-900 bg-clip-text text-transparent font-black',
        red: 'bg-gradient-to-r from-red-500 to-rose-500 bg-clip-text text-transparent',
        blue: 'bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent',
        emerald: 'bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent',
        purple: 'bg-gradient-to-r from-purple-500 to-indigo-500 bg-clip-text text-transparent',
        indigo: 'bg-gradient-to-r from-indigo-500 to-blue-500 bg-clip-text text-transparent',
      },
    },
    defaultVariants: {
      size: 'lg',
      gradient: 'none',
    },
  }
);

export interface PageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'>,
    VariantProps<typeof pageHeaderVariants> {
  /** Main title text */
  title: React.ReactNode;
  /** Optional subtitle text */
  subtitle?: React.ReactNode;
  /** Title size */
  titleSize?: VariantProps<typeof titleVariants>['size'];
  /** Title gradient color */
  titleGradient?: VariantProps<typeof titleVariants>['gradient'];
  /** Optional badge or action element next to the title area */
  badge?: React.ReactNode;
  /** Optional actions below the subtitle */
  actions?: React.ReactNode;
}

const PageHeader = React.forwardRef<HTMLElement, PageHeaderProps>(
  (
    {
      className,
      align,
      title,
      subtitle,
      titleSize,
      titleGradient,
      badge,
      actions,
      ...props
    },
    ref
  ) => {
    return (
      <section ref={ref} className="pt-20 pb-4 sm:pt-24 sm:pb-6" {...props}>
        <div className={cn(pageHeaderVariants({ align }), className)}>
          <h1 className={cn(titleVariants({ size: titleSize, gradient: titleGradient }))}>
            {title}
          </h1>
          {subtitle && (
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-slate-600 max-w-xl mx-auto">
              {subtitle}
            </p>
          )}
          {badge && <div className="mt-4">{badge}</div>}
          {actions && <div className="mt-4">{actions}</div>}
        </div>
      </section>
    );
  }
);
PageHeader.displayName = 'PageHeader';

export { PageHeader, pageHeaderVariants, titleVariants };
